/**
 * RACKET-LAB-FRONT-1 — RLAdminControlledProfiles (Jun 2026).
 *
 * Admin/QA panel at `/admin/racket-lab/controlled-profiles`. Read-only
 * listing of the 5 SS-8/CP-1.2 demo cohort accounts + 1 admin seed.
 * Does NOT expose passwords in plaintext — only describes which
 * credentials are documented in `/app/memory/test_credentials.md`,
 * which scope they have and how to obtain a fresh temp via
 * `/admin/testers`. Cero DB writes · cero schema apply · cero new endpoints.
 */
import React from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";
import { ShieldAlert, KeyRound, ExternalLink, ClipboardList, Lock } from "lucide-react";

const COHORT = [
  {
    key: "admin",
    email: "teamsoftwareinnovations@gmail.com",
    role: "admin",
    profile: "admin (global)",
    scope: "global",
    note: "Auto-seeded on backend startup · password documented in /app/memory/test_credentials.md.",
    accent: "rose",
  },
  {
    key: "staff_plain",
    email: "(provisioned ad-hoc)",
    role: "staff",
    profile: "staff plain",
    scope: "global / non-RL-scoped",
    note: "Provisioned ad-hoc via `/admin/testers`. No standing demo credential.",
    accent: "slate",
  },
  {
    key: "qa_manager",
    email: "ss8.demo.qamanager@teamcontrolled-qa.com",
    role: "qa_manager",
    profile: "QA Manager",
    scope: "global · read-only RL consoles",
    note: "SS-8/CP-1.3 demo cohort. Password in /app/memory/test_credentials.md.",
    accent: "indigo",
  },
  {
    key: "qa_tester",
    email: "ss8.demo.qatester@teamcontrolled-qa.com",
    role: "qa_tester",
    profile: "QA Tester",
    scope: "RL minimal · dashboard / tickets / calendar",
    note: "SS-8/CP-1.3 demo cohort. Auto-redirect to /admin/racket-lab.",
    accent: "indigo",
  },
  {
    key: "shop_manager",
    email: "ss8.demo.shopmanager@teamcontrolled-qa.com",
    role: "staff (RL shop_manager)",
    profile: "RL shop_manager",
    scope: "SS8 Demo Org · SS8 Demo Shop",
    note: "SS-8/CP-1.3 demo cohort. Auto-redirect to /admin/racket-lab.",
    accent: "emerald",
  },
  {
    key: "operator",
    email: "ss8.demo.operator@teamcontrolled-qa.com",
    role: "staff (RL operator)",
    profile: "RL operator",
    scope: "SS8 Demo Org · Demo Operator SS8",
    note: "SS-8/CP-1.3 demo cohort. Auto-redirect to /admin/racket-lab.",
    accent: "emerald",
  },
  {
    key: "institutional_observer",
    email: "(provisioned ad-hoc)",
    role: "institutional_observer",
    profile: "Institutional Observer",
    scope: "read-only stats dashboards",
    note: "Provisioned ad-hoc via `/admin/testers` (admin-only) · auto-redirect to /admin/stats-advanced.",
    accent: "slate",
  },
];

const ACCENTS = {
  emerald: "border-emerald-200 bg-emerald-50/40",
  amber: "border-amber-200 bg-amber-50/40",
  indigo: "border-indigo-200 bg-indigo-50/40",
  rose: "border-rose-200 bg-rose-50/40",
  slate: "border-slate-200 bg-slate-50/40",
};

const RLAdminControlledProfiles = () => {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const isES = lang === "es";
  const role = user?.role;
  if (role !== "admin" && role !== "staff" && role !== "qa_manager") {
    return <Navigate to="/admin" replace />;
  }

  const COHORT_I18N = {
    admin: {
      profile: isES ? "admin (global)" : "admin (global)",
      scope: isES ? "global" : "global",
      note: isES
        ? "Auto-sembrado al arranque del backend · contraseña documentada en /app/memory/test_credentials.md."
        : "Auto-seeded on backend startup · password documented in /app/memory/test_credentials.md.",
    },
    staff_plain: {
      profile: isES ? "staff plain" : "staff plain",
      scope: isES ? "global / no-RL-scoped" : "global / non-RL-scoped",
      note: isES
        ? "Aprovisionado ad-hoc desde `/admin/testers`. Sin credencial demo permanente."
        : "Provisioned ad-hoc via `/admin/testers`. No standing demo credential.",
    },
    qa_manager: {
      profile: isES ? "QA Manager" : "QA Manager",
      scope: isES ? "global · consolas RL en sólo lectura" : "global · read-only RL consoles",
      note: isES
        ? "Cohorte demo SS-8/CP-1.3. Contraseña en /app/memory/test_credentials.md."
        : "SS-8/CP-1.3 demo cohort. Password in /app/memory/test_credentials.md.",
    },
    qa_tester: {
      profile: isES ? "QA Tester" : "QA Tester",
      scope: isES ? "RL mínimo · dashboard / tickets / calendario" : "RL minimal · dashboard / tickets / calendar",
      note: isES
        ? "Cohorte demo SS-8/CP-1.3. Auto-redirige a /admin/racket-lab."
        : "SS-8/CP-1.3 demo cohort. Auto-redirect to /admin/racket-lab.",
    },
    shop_manager: {
      profile: isES ? "RL shop_manager" : "RL shop_manager",
      scope: isES ? "Org Demo SS8 · Tienda Demo SS8" : "SS8 Demo Org · SS8 Demo Shop",
      note: isES
        ? "Cohorte demo SS-8/CP-1.3. Auto-redirige a /admin/racket-lab."
        : "SS-8/CP-1.3 demo cohort. Auto-redirect to /admin/racket-lab.",
    },
    operator: {
      profile: isES ? "RL operario" : "RL operator",
      scope: isES ? "Org Demo SS8 · Operario Demo SS8" : "SS8 Demo Org · Demo Operator SS8",
      note: isES
        ? "Cohorte demo SS-8/CP-1.3. Auto-redirige a /admin/racket-lab."
        : "SS-8/CP-1.3 demo cohort. Auto-redirect to /admin/racket-lab.",
    },
    institutional_observer: {
      profile: isES ? "Observador Institucional" : "Institutional Observer",
      scope: isES ? "dashboards de stats en sólo lectura" : "read-only stats dashboards",
      note: isES
        ? "Aprovisionado ad-hoc desde `/admin/testers` (sólo admin) · auto-redirige a /admin/stats-advanced."
        : "Provisioned ad-hoc via `/admin/testers` (admin-only) · auto-redirect to /admin/stats-advanced.",
    },
  };
  const lbl = {
    profile: isES ? "Perfil" : "Profile",
    scope: isES ? "Alcance" : "Scope",
  };

  return (
    <div data-testid="rl-controlled-profiles" className="mx-auto max-w-5xl px-4 py-6">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold text-slate-900">{t("racketLabControlled.title")}</h1>
        <p className="mt-1 text-xs text-slate-500">
          {t("racketLabControlled.subtitle")}
        </p>
      </header>

      <div data-testid="rl-controlled-banner" className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4">
        <div className="flex items-start gap-3">
          <ShieldAlert className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-700" />
          <div className="text-xs text-amber-900">
            <p className="font-semibold">{t("racketLabControlled.bannerTitle")}</p>
            <p className="mt-1 leading-5">{t("racketLabControlled.bannerBody")}</p>
          </div>
        </div>
      </div>

      <section className="mb-6 grid gap-3 sm:grid-cols-2" data-testid="rl-controlled-grid">
        {COHORT.map((c) => {
          const tx = COHORT_I18N[c.key] || { profile: c.profile, scope: c.scope, note: c.note };
          return (
            <article key={c.key} data-testid={`rl-controlled-${c.key}`} className={`rounded-xl border ${ACCENTS[c.accent]} p-4 text-xs`}>
              <div className="flex items-center justify-between gap-2">
                <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-700 ring-1 ring-slate-200">{c.role}</span>
                <ClipboardList className="h-3.5 w-3.5 text-slate-400" />
              </div>
              <p className="mt-2 font-mono text-[11px] text-slate-900">{c.email}</p>
              <p className="mt-1 text-[11px] text-slate-700"><strong>{lbl.profile}:</strong> {tx.profile}</p>
              <p className="text-[11px] text-slate-700"><strong>{lbl.scope}:</strong> {tx.scope}</p>
              <p className="mt-2 flex items-center gap-1 rounded bg-white/80 p-2 text-[10px] italic text-slate-600">
                <Lock className="h-3 w-3" /> {tx.note}
              </p>
            </article>
          );
        })}
      </section>

      <section data-testid="rl-controlled-actions" className="mb-6 rounded-xl border border-slate-200 bg-white p-4 text-xs">
        <h2 className="mb-2 text-sm font-semibold text-slate-900">{t("racketLabControlled.actionsTitle")}</h2>
        <ul className="space-y-1">
          <li className="flex items-center gap-2">
            <KeyRound className="h-3.5 w-3.5 text-emerald-700" />
            <Link to="/admin/testers" className="text-emerald-700 hover:underline" data-testid="rl-controlled-link-testers">
              /admin/testers — {t("racketLabControlled.actionsTesters")}
            </Link>
            <ExternalLink className="h-3 w-3 text-slate-400" />
          </li>
          <li className="flex items-center gap-2">
            <KeyRound className="h-3.5 w-3.5 text-emerald-700" />
            <span>{t("racketLabControlled.actionsCredentialsFile")} <span className="font-mono">/app/memory/test_credentials.md</span></span>
          </li>
          <li className="flex items-center gap-2">
            <Lock className="h-3.5 w-3.5 text-rose-700" />
            <span className="text-rose-800">{t("racketLabControlled.actionsNoPlaintext")}</span>
          </li>
        </ul>
      </section>

      <footer data-testid="rl-controlled-footer-legal" className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-3 text-[11px] italic text-slate-600">
        {t("racketLabControlled.footerLegal")}
      </footer>
    </div>
  );
};

export default RLAdminControlledProfiles;
